import { StatsGrid } from "./components/StatsGrid"
import { RecentActivity } from "./components/RecentActivity"
import { ServiceHealth } from "./components/ServiceHealth"
import { UsersSummaryCard } from "./components/UsersSummaryCard"
import { RolesSummaryCard } from "./components/RolesSummaryCard"
import { TasksSummaryCard } from "./components/TasksSummaryCard"
import { UserStatsCard } from "./components/UserStatsCard"
import { RbacInsightsCard } from "./components/RbacInsightsCard"
import { PascoAnalyticsCard } from "./components/PascoAnalyticsCard"
import { LayoutDashboard, Info } from "lucide-react"
import { useStats } from "@src/presentation/hooks/stats"
import { mockTasks } from "@src/shared/data/mockData"
import { ProtectedComponent } from "@src/presentation/components/ProtectedComponent"
import { PERMISSIONS } from "@src/shared/constants/permissions"
import { usePermission } from "@src/presentation/hooks/usePermission"
import { Card, CardBody } from "@heroui/react"

export default function Dashboard() {
    // Check if user has any dashboard permissions
    const { hasAnyPermission, hasPermission } = usePermission()
    const hasAnyDashboardPermissions = hasAnyPermission([
        PERMISSIONS.STATS_READ,
        PERMISSIONS.PASCO_READ,
        PERMISSIONS.USER_READ,
        PERMISSIONS.ROLE_READ,
        PERMISSIONS.TASK_READ,
    ])

    const canViewStats = hasPermission(PERMISSIONS.STATS_READ)
    // const canViewPascos = hasPermission(PERMISSIONS.PASCO_READ)

    // Fetch stats from API - only if user has permission
    const { data: stats } = useStats({
        refetchInterval: 500000, // Auto-refresh every 5 minutes
        enabled: canViewStats,
    })

    // Fetch PASCOs data - only if user has permission
    // const { data: pascos = [], isLoading: pascosLoading, error: pascosError } = useListAllPascos({
    //     enabled: canViewPascos,
    // })

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b sticky top-0 border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                                <LayoutDashboard className="text-white h-4 w-4 sm:h-5 sm:w-5" />
                            </div>
                            <h1 className="text-lg sm:text-xl font-semibold text-foreground">Dashboard</h1>
                        </div>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                            <svg className="h-3 sm:h-4 w-3 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <span className="hidden sm:inline">Last updated: {stats?.timestamp ? new Date(stats.timestamp).toLocaleString() : new Date().toLocaleString()}</span>
                            <span className="sm:hidden">{stats?.timestamp ? new Date(stats.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString()}</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-8">
                    {/* Info card for users with limited permissions */}
                    {!hasAnyDashboardPermissions && (
                        <Card className="border border-primary/30 bg-primary/5 rounded shadow-none">
                            <CardBody className="p-6">
                                <div className="flex gap-4">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                                        <Info className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-foreground mb-1">Limited Dashboard Access</h3>
                                        <p className="text-sm text-muted-foreground">
                                            Some dashboard sections are hidden because you don't have the required permissions to view them.
                                            You can still access the sections and features that are visible to you. Contact your administrator
                                            if you need access to additional features.
                                        </p>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {/* Stats Grid - Key Metrics Overview */}
                    <ProtectedComponent permission={PERMISSIONS.STATS_READ}>
                        <StatsGrid />
                    </ProtectedComponent>

                    {/* Enhanced Analytics - Users and RBAC Insights */}
                    <ProtectedComponent permission={PERMISSIONS.STATS_READ}>
                        <div className="grid gap-8 lg:grid-cols-2">
                            <UserStatsCard />
                            <RbacInsightsCard />
                        </div>
                    </ProtectedComponent>

                    {/* PASCO Analytics */}
                    <ProtectedComponent permission={PERMISSIONS.STATS_READ}>
                        <PascoAnalyticsCard />
                    </ProtectedComponent>

                    {/* Activity and Health */}
                    <div className="grid gap-8 lg:grid-cols-2">
                        <ProtectedComponent permission={PERMISSIONS.TASK_READ}>
                            <RecentActivity
                                tasks={mockTasks.map(task => ({
                                    ...task,
                                    status: task.status as "completed" | "pending" | "failed"
                                }))}
                                limit={5}
                            />
                        </ProtectedComponent>
                        {/* ServiceHealth is visible to all authenticated users */}
                        <ServiceHealth />
                    </div>

                    {/* PASCOs Summary - Shows recent 5 PASCOs */}
                    {/* <ProtectedComponent permission={PERMISSIONS.PASCO_READ}>
                        <PascosSummaryCard
                            pascos={pascos}
                            isLoading={pascosLoading}
                            error={pascosError}
                            limit={5}
                        />
                    </ProtectedComponent> */}

                    {/* Tasks Summary - Shows recent 5 tasks */}
                    <ProtectedComponent permission={PERMISSIONS.TASK_READ}>
                        <TasksSummaryCard
                            limit={5}
                            stats={stats ? {
                                total: stats.pasco_jobs.total_jobs,
                                pending: stats.pasco_jobs.status_breakdown.pending || 0,
                                processing: stats.pasco_jobs.status_breakdown.processing || 0,
                                completed: stats.pasco_jobs.status_breakdown.completed || 0,
                                failed: stats.pasco_jobs.status_breakdown.failed || 0,
                            } : undefined}
                        />
                    </ProtectedComponent>

                    {/* Users and Roles Summary Cards */}
                    <div className="grid gap-8 lg:grid-cols-2">
                        <ProtectedComponent permission={PERMISSIONS.USER_READ}>
                            <UsersSummaryCard limit={5} />
                        </ProtectedComponent>
                        <ProtectedComponent permission={PERMISSIONS.ROLE_READ}>
                            <RolesSummaryCard />
                        </ProtectedComponent>
                    </div>
                </div>
            </main>
        </div>
    )
}
